/**
 * Query keys of everything that shows the company's own details — GST
 * state, GSTIN, address, PT state — and the one helper that refreshes them
 * after a save (usability review 2026-10-20: a GSTIN saved from the Books
 * banner still showed "Not set" on Settings → Workspace until a reload, and
 * the address banner stayed after the address was saved).
 *
 * The same values are edited in two places (Settings → Workspace via
 * PATCH /tenant/settings, and the Books "GST details" banner via PATCH
 * /accounting/gst-profile) and read in three:
 *
 *  - TENANT_SETTINGS_QUERY_KEY  GET /tenant/settings — the Workspace form
 *                               (keep equal to payrollKeys.tenantSettings())
 *  - GST_PROFILE_QUERY_KEY      GET /accounting/gst-profile — invoice / bill
 *                               forms and the Books banner (keep equal to
 *                               accountingKeys.gstProfile())
 *  - SETUP_STATUS_QUERY_KEY     GET /setup/status — the Company profile card
 *                               and the setup checklist
 *
 * Dependency-free (type-only imports) so `npm run test:unit` can load it.
 */

import type { QueryClient } from "@tanstack/react-query";
import { SETUP_STATUS_QUERY_KEY } from "./headcount-queries.ts";

export const TENANT_SETTINGS_QUERY_KEY = ["tenant", "settings"] as const;
export const GST_PROFILE_QUERY_KEY = ["accounting", "gst-profile"] as const;

export const COMPANY_PROFILE_QUERY_KEYS = [
  TENANT_SETTINGS_QUERY_KEY,
  GST_PROFILE_QUERY_KEY,
  SETUP_STATUS_QUERY_KEY,
] as const;

/**
 * Refresh every reader of the company's details. Resolves once the active
 * ones have refetched, so a caller can show its "saved" toast after the
 * other cards already show the new values.
 */
export async function invalidateCompanyProfile(
  qc: Pick<QueryClient, "invalidateQueries">,
): Promise<void> {
  await Promise.all(COMPANY_PROFILE_QUERY_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })));
}
